import {
  DEFAULT_LOCALE,
  isRtlLocale,
  type Locale,
} from "@/lib/i18n/config";
import { LEGAL_ADDENDA_LOADERS } from "@/content/legal-addenda-loaders";
import {
  composeLegalPolicyHtml,
  overlayHtmlForPage,
  resolveLegalOverlayCountry,
  type LegalCountryAddendaFile,
} from "@/content/legal-country-overlay";
import enCache from "@/content/legal-policies.cache/en.json";
import esCache from "@/content/legal-policies.cache/es.json";
import meta from "@/content/legal-policies.cache/meta.json";

/** Hub + secondary legal pages backed by the compliance snapshot export. */
export type LegalPolicyKey =
  | "privacy"
  | "terms"
  | "cookies"
  | "impressum"
  | "accessibility"
  | "return_policy"
  | "shipping_policy"
  | "eula"
  | "disclaimer"
  | "acceptable_use"
  | "dpa"
  | "sla";

type LegalPolicyLocaleSnapshot = {
  requestedLocale: Locale;
  resolvedLocale: string;
  createdLocales: string[];
  content: string;
  contentSha256: string;
};

type LegalLocaleFile = {
  locale: string;
  policies: Partial<
    Record<
      LegalPolicyKey,
      {
        name: string;
        templateType: string;
        uuid: string;
        documentVersion: number;
        publishedDocumentVersionId: number;
        locale: LegalPolicyLocaleSnapshot;
      }
    >
  >;
};

type LegalPolicyMeta = {
  schemaVersion: number;
  source: string;
  complianceVersion?: string;
  websiteUuid: string;
  syncedAt: string;
  sourceMeta?: {
    exporter?: string;
    bundle?: string;
    snippetUrl?: string;
    contentUrlTemplate?: string;
  };
  policies: Partial<
    Record<
      LegalPolicyKey,
      {
        name: string;
        templateType: string;
        uuid: string;
        documentVersion: number;
        publishedDocumentVersionId: number;
      }
    >
  >;
};

const snapshotMeta = meta as LegalPolicyMeta;

const localeCaches = new Map<string, LegalLocaleFile>([
  ["en", enCache as LegalLocaleFile],
  ["es", esCache as LegalLocaleFile],
]);

const addendaCaches = new Map<string, LegalCountryAddendaFile>();

const LEGAL_CACHE_LOADERS: Record<string, () => Promise<LegalLocaleFile>> = {
  fr: () => import("./legal-policies.cache/fr.json").then((m) => m.default as LegalLocaleFile),
  de: () => import("./legal-policies.cache/de.json").then((m) => m.default as LegalLocaleFile),
  it: () => import("./legal-policies.cache/it.json").then((m) => m.default as LegalLocaleFile),
  pt: () => import("./legal-policies.cache/pt.json").then((m) => m.default as LegalLocaleFile),
  "pt-BR": () =>
    import("./legal-policies.cache/pt-BR.json").then((m) => m.default as LegalLocaleFile),
  nl: () => import("./legal-policies.cache/nl.json").then((m) => m.default as LegalLocaleFile),
  pl: () => import("./legal-policies.cache/pl.json").then((m) => m.default as LegalLocaleFile),
  sv: () => import("./legal-policies.cache/sv.json").then((m) => m.default as LegalLocaleFile),
  da: () => import("./legal-policies.cache/da.json").then((m) => m.default as LegalLocaleFile),
  no: () => import("./legal-policies.cache/no.json").then((m) => m.default as LegalLocaleFile),
  fi: () => import("./legal-policies.cache/fi.json").then((m) => m.default as LegalLocaleFile),
  cs: () => import("./legal-policies.cache/cs.json").then((m) => m.default as LegalLocaleFile),
  sk: () => import("./legal-policies.cache/sk.json").then((m) => m.default as LegalLocaleFile),
  sl: () => import("./legal-policies.cache/sl.json").then((m) => m.default as LegalLocaleFile),
  hr: () => import("./legal-policies.cache/hr.json").then((m) => m.default as LegalLocaleFile),
  hu: () => import("./legal-policies.cache/hu.json").then((m) => m.default as LegalLocaleFile),
  ro: () => import("./legal-policies.cache/ro.json").then((m) => m.default as LegalLocaleFile),
  el: () => import("./legal-policies.cache/el.json").then((m) => m.default as LegalLocaleFile),
  ca: () => import("./legal-policies.cache/ca.json").then((m) => m.default as LegalLocaleFile),
  ru: () => import("./legal-policies.cache/ru.json").then((m) => m.default as LegalLocaleFile),
  uk: () => import("./legal-policies.cache/uk.json").then((m) => m.default as LegalLocaleFile),
  tr: () => import("./legal-policies.cache/tr.json").then((m) => m.default as LegalLocaleFile),
  ar: () => import("./legal-policies.cache/ar.json").then((m) => m.default as LegalLocaleFile),
  he: () => import("./legal-policies.cache/he.json").then((m) => m.default as LegalLocaleFile),
  ur: () => import("./legal-policies.cache/ur.json").then((m) => m.default as LegalLocaleFile),
  hi: () => import("./legal-policies.cache/hi.json").then((m) => m.default as LegalLocaleFile),
  bn: () => import("./legal-policies.cache/bn.json").then((m) => m.default as LegalLocaleFile),
  gu: () => import("./legal-policies.cache/gu.json").then((m) => m.default as LegalLocaleFile),
  mr: () => import("./legal-policies.cache/mr.json").then((m) => m.default as LegalLocaleFile),
  ta: () => import("./legal-policies.cache/ta.json").then((m) => m.default as LegalLocaleFile),
  te: () => import("./legal-policies.cache/te.json").then((m) => m.default as LegalLocaleFile),
  pa: () => import("./legal-policies.cache/pa.json").then((m) => m.default as LegalLocaleFile),
  ja: () => import("./legal-policies.cache/ja.json").then((m) => m.default as LegalLocaleFile),
  ko: () => import("./legal-policies.cache/ko.json").then((m) => m.default as LegalLocaleFile),
  "zh-Hans": () =>
    import("./legal-policies.cache/zh-Hans.json").then((m) => m.default as LegalLocaleFile),
  "zh-Hant": () =>
    import("./legal-policies.cache/zh-Hant.json").then((m) => m.default as LegalLocaleFile),
  id: () => import("./legal-policies.cache/id.json").then((m) => m.default as LegalLocaleFile),
  ms: () => import("./legal-policies.cache/ms.json").then((m) => m.default as LegalLocaleFile),
  vi: () => import("./legal-policies.cache/vi.json").then((m) => m.default as LegalLocaleFile),
  th: () => import("./legal-policies.cache/th.json").then((m) => m.default as LegalLocaleFile),
};

/** Phantom mailboxes that must never appear as live contact targets. */
const LEGACY_PUBLIC_MAILBOXES = [
  "privacy@runwayready.ai",
  "dpo@runwayready.ai",
  "legal@runwayready.ai",
  "accessibility@runwayready.ai",
  "partners@runwayready.app",
  "partners@runwayready.ai",
] as const;

const PUBLIC_CONTACT_EMAIL = "contact@runwayready.ai";

/** Map legacy policy mailto targets to the single real public mailbox. */
export function rewriteLegalContactEmails(html: string): string {
  let next = html;
  for (const address of LEGACY_PUBLIC_MAILBOXES) {
    const escaped = address.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    next = next.replace(new RegExp(escaped, "gi"), PUBLIC_CONTACT_EMAIL);
  }
  return next;
}

export async function loadLegalLocaleCache(locale: Locale): Promise<LegalLocaleFile> {
  const hit = localeCaches.get(locale);
  if (hit) return hit;
  const loader = LEGAL_CACHE_LOADERS[locale];
  if (!loader) return localeCaches.get(DEFAULT_LOCALE)!;
  try {
    const file = await loader();
    localeCaches.set(locale, file);
    return file;
  } catch {
    return localeCaches.get(DEFAULT_LOCALE)!;
  }
}

function snapshotFromCache(
  page: LegalPolicyKey,
  locale: Locale,
  cache: LegalLocaleFile | undefined,
): LegalPolicyLocaleSnapshot {
  const fallback = localeCaches.get(DEFAULT_LOCALE);
  const node = cache?.policies[page]?.locale ?? fallback?.policies[page]?.locale;
  if (!node) {
    throw new Error(`Missing legal policy snapshot for page: ${page}`);
  }
  const resolvedLocale = node.resolvedLocale || cache?.locale || DEFAULT_LOCALE;
  return {
    ...node,
    content: rewriteLegalContactEmails(node.content),
    requestedLocale: locale,
    resolvedLocale,
  };
}

export async function loadLegalCountryOverlay(
  country: string | null | undefined,
): Promise<LegalCountryAddendaFile | null> {
  const code = resolveLegalOverlayCountry(country);
  if (!code) return null;
  const hit = addendaCaches.get(code);
  if (hit) return hit;
  const loader = LEGAL_ADDENDA_LOADERS[code];
  if (!loader) return null;
  try {
    const file = (await loader()).default;
    addendaCaches.set(code, file);
    return file;
  } catch {
    return null;
  }
}

export function getLegalPolicySnapshot(
  page: LegalPolicyKey,
  locale: Locale,
  overlay?: LegalCountryAddendaFile | null,
): LegalPolicyLocaleSnapshot {
  const baseline = snapshotFromCache(page, locale, localeCaches.get(locale));
  const extra = rewriteLegalContactEmails(overlayHtmlForPage(overlay, locale, page));
  if (!extra) return baseline;
  return {
    ...baseline,
    content: composeLegalPolicyHtml(baseline.content, extra),
  };
}

export function getLegalPolicyTitle(page: LegalPolicyKey, locale: Locale): string {
  const cache = localeCaches.get(locale) ?? localeCaches.get(DEFAULT_LOCALE);
  const policy = cache?.policies[page];
  const html = policy?.locale.content ?? "";
  const m = html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (m?.[1]) return m[1].replace(/<[^>]+>/g, "").trim();
  return snapshotMeta.policies[page]?.name ?? page;
}

export function getLegalSyncIsoDate(): string {
  return snapshotMeta.syncedAt;
}

export function getLegalSyncDateLabel(locale: Locale): string {
  const date = new Date(snapshotMeta.syncedAt);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function listLegalPolicyPages(): LegalPolicyKey[] {
  return (Object.keys(snapshotMeta.policies) as LegalPolicyKey[]).filter(
    (k) => Boolean(snapshotMeta.policies[k]),
  );
}

export function legalDir(locale: Locale): "rtl" | "ltr" {
  return isRtlLocale(locale) ? "rtl" : "ltr";
}
